// on Mac, Alt + C generates ç by default, so watch for both
export const isAltC = (e) => (e.altKey || e.metaKey)
  && (e.key.toLowerCase() === 'c' || e.key.toLowerCase() === 'ç');

export const isAltR = (e) => (e.altKey || e.metaKey)
  && (e.key.toLowerCase() === 'r' || e.key.toLowerCase() === '®');

export const isAltE = (e) => (e.altKey || e.metaKey)
  && (e.key.toLowerCase() === 'e' || e.key.toLowerCase() === '´');

// need to check this in mac, not able to get proper info for mac
export const isAltA = (e) => (e.altKey || e.metaKey)
  && (e.key.toLowerCase() === 'a' || e.key.toLowerCase() === 'a');

export const isAltP = (e) => (e.altKey || e.metaKey)
  && (e.key.toLowerCase() === 'p' || e.key.toLowerCase() === 'p');

export const isAltS = (e) => (e.altKey || e.metaKey)
  && (e.key.toLowerCase() === 's' || e.key.toLowerCase() === 's');

export const isAltI = (e) => (e.altKey || e.metaKey)
  && (e.key.toLowerCase() === 'i' || e.key.toLowerCase() === 'i');

export const isAltB = (e) => (e.altKey || e.metaKey)
  && (e.key.toLowerCase() === 'b' || e.key.toLowerCase() === 'b');

export const isTabOrPlus = (e) => (!e.shiftKey && e.key === 'Tab')
  || (e.key === '+');

// Insert key on Windows, Ctrl + I on Mac
export const isInsert = (e) => e.key === 'Insert' || (e.key === 'i' && e.ctrlKey);

export const isEnter = (e) => e.key === 'Enter';

export const isEquals = (e) => e.key === '=';

export const isShift = (e) => e.key === '"' && e.shiftKey && e.ctrlKey;

// todo - can't intercept these in a browser.. something else?
export const isCreditFnKey = (e) => e.key === 'F4';
export const isCashFnKey = (e) => e.key === 'F5';
export const isCheckFnKey = (e) => e.key === 'F6';
export const isFnNineKey = (e) => e.key === 'F9';
export const isFnTenKey = (e) => e.key === 'F10';

export const isTaxOrPayFnKey = (e) => e.key === 'F11';

export const focusBySelector = (selector, whichOne = 'last') => {
  const allAmounts = document.querySelectorAll(selector);
  if (allAmounts.length > 0) {
    let focusMe;
    if (whichOne === 'last') {
      focusMe = allAmounts[allAmounts.length - 1];
    } else if (whichOne === 'first') {
      [focusMe] = allAmounts;
    }
    // console.log(focusMe);
    focusMe.focus();
  }
};

/**
 * Commit whatever the user has typed into the focused control before a save handler
 * reads the record.
 *
 * v-combobox does not push typed text into its v-model on every keystroke the way
 * v-text-field does - the text sits in the component's internal search buffer
 * (`internalSearch`) and is only written to the model when Vuetify decides the field
 * is done being edited.
 *
 * The catch is that Vuetify's select family tracks focus with its OWN `isFocused`
 * flag, not the DOM's. VSelect.onBlur only re-emits the event; it never clears the
 * flag, so a DOM blur commits nothing. The commit runs off the `isFocused` watcher
 * in VAutocomplete, which only fires when something calls the component's `blur()`
 * (tabbing out does it via onTabDown - which is why tabbing off first works).
 *
 * Clicking SAVE does none of that: the mousedown moves DOM focus to the button, so
 * by the time the click handler runs `document.activeElement` is the button and the
 * combobox still thinks it is focused with the typed text uncommitted. A save that
 * reads the record at that point silently ships the value from before the last edit.
 *
 * So: ask any still-"focused" Vuetify control in this subtree to blur itself, then
 * wait a tick for the watcher to write the value through.
 *
 * Usage: `await flushFocusedField(this);` as the first line of a save handler.
 */
export const flushFocusedField = async (vm) => {
  if (!vm) return;
  const blurFocusedControls = (component) => {
    (component.$children || []).forEach((child) => {
      if (child.isFocused === true && typeof child.blur === 'function') {
        child.blur();
      }
      blurFocusedControls(child);
    });
  };
  blurFocusedControls(vm);

  // plain inputs that rewrite their value in an @blur handler (amount formatting)
  // are driven by real DOM focus, so they still need this
  const focused = document.activeElement;
  if (focused && typeof focused.blur === 'function') {
    focused.blur();
  }

  await vm.$nextTick();
};

/**
 * Would Enter press the control the user is standing on? A button/link takes Enter as
 * "press me", which is both how a dialog's own action buttons get used from the keyboard
 * and why a dialog must never cancel Enter's default action while one is focused.
 */
export const enterPressesFocusedControl = (e) => {
  const target = e && e.target;
  if (!target || typeof target.closest !== 'function') return false;
  return !!target.closest('button, a[href]');
};

/**
 * Does Enter belong to the control the user is standing on rather than to the dialog
 * around it?
 *
 * Saving (or closing) on those would swallow the keystroke: the newline never gets
 * typed, the highlighted list item never gets picked.
 *
 * `treatButtonsAsOwners` - crud-2.0 lets a focused button/link keep Enter, because that
 * is how its record dialogs open their sub-dialogs (status code, fees, comments,
 * payments). crud v1 must NOT: its Save/Apply buttons only ever save through the
 * dialog's own Enter handler - saveOnClick deliberately ignores the `detail === 0`
 * click the browser synthesizes for a keyboard press - so handing Enter back to the
 * button there would stop tab-to-Save from saving at all.
 */
export const enterBelongsToFocusedControl = (e, { treatButtonsAsOwners = true } = {}) => {
  const target = e && e.target;
  if (!target || typeof target.closest !== 'function') return false;
  // Remarks / comment boxes - Enter inserts a newline.
  if (target.tagName === 'TEXTAREA' || target.isContentEditable) return true;
  if (treatButtonsAsOwners && enterPressesFocusedControl(e)) return true;
  // v-select / v-autocomplete / v-combobox - Enter opens the list or picks the
  // highlighted item. aria-haspopup="listbox" sits on their .v-input__slot.
  // aria-expanded="true" covers any other open v-menu activator (date pickers),
  // which is only set while that menu is actually showing.
  return !!target.closest('[aria-haspopup="listbox"], [aria-expanded="true"]');
};

export const selectInputTextBySelector = (selector, whichOne = 'last') => {
  const allSelectors = document.querySelectorAll(selector);
  if (allSelectors.length > 0) {
    let selectMe;
    if (whichOne === 'last') {
      selectMe = allSelectors[allSelectors.length - 1];
    } else if (whichOne === 'first') {
      [selectMe] = allSelectors;
    }
    selectMe.select();
  }
};

export const amountToNumber = (amount) => {
  let amountStr = '';
  // this checks if not undefined and if not null
  if (!(amount == null)) {
    amountStr = (amount.toString()).replace(/,/g, '');
  }
  // parse a float, truncate 2 decimals as a string, re-parse
  // https://stackoverflow.com/a/4435190/4161426 maybe slow? 🤷🏻‍♂️
  const amountFloat = parseFloat(parseFloat(amountStr).toFixed(2));
  return Number.isNaN(amountFloat) ? 0.00 : amountFloat;
};
export const amountToNumberWithEmpty = (amount) => {
  let amountStr = '';
  // this checks if not undefined and if not null
  if (!(amount == null || amount === '')) {
    amountStr = (amount.toString()).replace(/,/g, '');
  }
  // parse a float, truncate 2 decimals as a string, re-parse
  // https://stackoverflow.com/a/4435190/4161426 maybe slow? 🤷🏻‍♂️
  const amountFloat = parseFloat(parseFloat(amountStr).toFixed(2));
  return Number.isNaN(amountFloat) ? 0.00 : amountFloat;
};
export const roundToNearestCent = (amount) => {
  if (amount == null || amount === '') {
    return '0.00';
  }
  const result = Number(`${Math.round(`${amount}e2`)}e-2`);
  return Number.isNaN(result) ? '0.00' : result.toFixed(2);
};
// const res = Math.round(((Number(amount)) + Number.EPSILON) * 100) / 100;
// const roundToDeciaml = res ? res.toFixed(2) : '0.00';
// return roundToDeciaml;

export const toFixedWithOutRoundOff = (value) => {
  const result = Number((Math.floor(value * 100) / 100).toFixed(2));
  return result;
};
export const amountFormat = (amount, withCommas = true) => {
  /* Sometime 'amountFormat' get called for already formatted amount */
  const amountInNumber = amountToNumber(amount);
  let amountStr = amountInNumber.toFixed(2);
  if (withCommas) {
    amountStr = amountStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  return amountStr;
};

export const amountFormatCanbeEmpty = (amount, withCommas = true) => {
  let amountStr = '';
  if ((amount === null || amount === '')) {
    amountStr = '';
    // eslint-disable-next-line brace-style
  }
  /* Sometime 'amountFormat' get called for already formatted amount */
  else {
    const amountInNumber = amountToNumber(amount);
    amountStr = amountInNumber.toFixed(2);
    if (withCommas) {
      amountStr = amountStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }
  return amountStr;
};

export const shortenCharacterFormat = (val, header, item) => {
  let returnVal = '';
  const chaLength = header.shortenCharacterlength || 20;
  if (header.isItSitusAddress && Number(val) === 0) {
    if (item.taxType === 'R') returnVal = item.acres ? `${item.acres} Acres` : null;
    else if (item.taxType === 'S') returnVal = item.schoolDistrict ? item.schoolDistrict : null;
  } else {
    returnVal = (val && val.length) > chaLength ? `${val.substring(0, chaLength)}...` : val;
  }
  return returnVal;
};

// export const getFormatPidPositions = (pidValue) => {
//   const arrFormat = Array.from(pidValue || []);
//   let arrLengths = [];
//   if (arrFormat.length) {
//     arrLengths = arrFormat
//       .map((dt, ind) => (dt === '-' ? ind : ''))
//       .filter((ft) => ft !== '');
//   }
//   return arrLengths;
// };

export const pIdFormat = (pidValue, formatPidPositions = '') => {
  const formatPid = pidValue && pidValue.toLocaleString() && typeof (pidValue === 'string') ? pidValue : '';

  /* ------ Vignesh code ---------- */
  // if (pidValue) {
  //   let i = 0;
  //   const pidLen = pidValue.length;
  // eslint-disable-next-line max-len
  //   if (formatPidPositions && formatPidPositions.toLocaleString() && typeof (formatPidPositions === 'string')) {
  //     // this shortenPidPos is necessary or else it adds ---- at end
  //     let shortenPidPos = formatPidPositions.substring(0, pidLen);
  //     // to get dashes length
  //     const getDashesLen = formatPidPositions.match(/-/gi).length;
  //     // to get shortenpidpos lenght with dashes length and 1 for error handling
  //     const getShortenPidLength = shortenPidPos.length + (getDashesLen + 1);
  //     // here x strings will be generated
  //     const generatedAdditionalString = 'x'.repeat(pidLen);
  //     if (pidLen !== getShortenPidLength) {
  //       // here the the x will be added to end of string
  //       shortenPidPos = `${shortenPidPos}${generatedAdditionalString}`;
  //     }
  //     // eslint-disable-next-line

  // eslint-disable-next-line max-len
  //     formatPid = shortenPidPos ? shortenPidPos.replace(/x/g, () => pidValue[i++] || '') : pidValue;
  //   }
  // }

  /* ---End of  Vignesh code ----- */
  if (pidValue) {
    const tempFormat = formatPidPositions ? formatPidPositions.split('-') : [];
    let str = '';
    let index = 0;
    // eslint-disable-next-line array-callback-return
    tempFormat.map((val) => {
      const checkLength = val.length;
      const tempLength = index + checkLength;
      const pidConcat = pidValue.slice(index, tempLength);
      if (pidConcat) {
        str += `${pidConcat}-`;
      }

      index += checkLength;
    });
    if ((index > 0) && (index < pidValue.length)) {
      str = str.replace(/-*$/, '') || pidValue;
      const totalLength = pidValue.length;
      const remainDigit = pidValue.slice(index, totalLength);
      str += `${remainDigit}`;
      return str;
    }
    return str.replace(/-*$/, '') || pidValue;
  }
  return formatPid;
  // const checkFormatIsAnArrayOrNot = formatPidPositions && formatPidPositions.length > 0
  //   ? [...formatPidPositions] : [];
  // const pid = typeof (pidValue) === 'string' ? [...pidValue] : [];
  // pid.map((ft, ind) => {
  //   checkFormatIsAnArrayOrNot.map((gt) => {
  //     if (gt === ind) pid.splice(gt, 0, '-');
  //     return '';
  //   });
  //   return '';
  // });
  // return pid.join('');
};

export const zipDeFormat = (item) => {
  let val = null;
  if (item) {
    const alteredStr = item.toString().replace(/-/g, '');
    // val = Number.isNaN(Number(alteredStr)) ? null : Number(alteredStr);
    val = alteredStr;
  }
  return val;
};

/**
 * Coerce a ZIP code into a plain integer for the `zipCode: GraphQLInt` schema field.
 *
 * Do NOT use amountToNumber for ZIPs: it is a currency parser, so a "466.37" typed into the
 * CC Transaction List edit dialog persisted as a float and the next list load failed with
 * "Int cannot represent non-integer value: 466.37" (McCurtain, Jul 2026).
 *
 * Digit-strip rather than round — Math.round(466.37) is 466, but the digits spell 46637,
 * the real ZIP.
 *
 * zip+4 keeps all 9 digits, matching zipDeFormat below (dash stripped, digits kept) and how
 * POS credit records are already written. 9 digits still fits GraphQLInt's 2147483647 ceiling;
 * anything longer is junk (a phone number in the ZIP field) so it falls back to the leading 5.
 *
 * Mirrors the backend sanitizeZipCode in src/util/tmc-global.js.
 */
export const sanitizeZipCode = (zip) => {
  if (zip == null || zip === '') return null;
  const digits = zip.toString().replace(/\D/g, '');
  if (digits === '') return null;
  return parseInt(digits.length > 9 ? digits.slice(0, 5) : digits, 10);
};

/**
 * Display form of a stored zip: "730441234" -> "73044-1234".
 *
 * The Cloud stores a bare 5-digit zip when there is no +4, and dashing that yields a trailing
 * "73044-" (Asana 1217684595064215), so anything shorter than a zip+4 is returned as typed.
 * Kept in step with the backend copy in tmc-local-backend/src/util/tmc-global.js.
 */
export const zipFormat = (item) => {
  if (item) {
    const zip = item.toString().trim();
    if (zip.length < 6) {
      return zip;
    }
    return zip.split('').map((dt, ind) => (ind === 4 ? `${dt}-` : dt)).join('');
  }
  return '';
};

export const combinedNames = (item, header) => {
  const checkFormatIsAnArrayOrNot = header && header.combineTextFileds
    && header.combineTextFileds.length > 0 ? header.combineTextFileds : [];
  const comNames = checkFormatIsAnArrayOrNot.map((ft) => item[ft]);
  return comNames.join(' ');
};

export const hideAndShowFields = (value) => {
  let yOrEmpty = value;
  if (value && value.toLowerCase().includes('ax')) {
    yOrEmpty = '';
  }
  return yOrEmpty;
};

function roundNumber(number, places) {
  return +(`${Math.round(`${number}e+${places}`)}e-${places}`);
}

export const amountRoundOfToClosestDecimal = (amount, places) => {
  let parsedFloatAmount = parseFloat(amount);

  if (Number.isNaN(parsedFloatAmount)) {
    return 0;
  }
  const placesToRoundOff = places || 2;
  const amountDecimalVal = (parsedFloatAmount || '').toString().split('.')[1];
  for (let i = (amountDecimalVal || []).length; i >= placesToRoundOff; i -= 1) {
    parsedFloatAmount = roundNumber(parsedFloatAmount, i);
  }
  return parsedFloatAmount;
};

export const camelCaseToWords = (text) => {
  // importBankVoucher => Import Bank Voucher
  const result = text.replace(/([A-Z])/g, ' $1');
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
};
export const camelCaseEachToWords = (text) => {
  if (!text) return '';
  const result = text.replace(/\b(eft)|([a-z])([A-Z])|(-)([a-z])/g, (match, eft, lower, upper, hyphen, nextChar) => {
    if (eft) return 'EFT';
    if (hyphen) return `-${nextChar.toUpperCase()}`;
    return `${lower} ${upper}`;
  });
  return result
    .split(' ').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};
// example: ('this.that', { this: { that: 'a' } })
export const resolveSubProp = (prop, obj) => {
  const reducer = (acc, curr) => {
    const result = acc ? acc[curr] : null;
    return result;
  };
  // Note to future self
  // prop.split is not a function (if you put it under computed: {} vs methods: {})
  return prop.split('.').reduce(reducer, obj);
};

// Format a date/time (Date or String) as MM/DD/YYYY, hh:mm:ss AM/PM.
// Locale is pinned to en-US so the date order is stable regardless of the
// browser's locale (audit records previously drifted to dd/mm/yyyy on en-GB, etc.).
export const toLocalDateTime = (date = '0001-01-01', unitTestLocaleIfAny = 'en-US') => {
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleString(unitTestLocaleIfAny, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

export const isDate = (dateString) => {
  if ((new Date(dateString)).getDate()) return true;
  return false;
};

export const isIsoDate = (str) => {
  if (!/\d{4}-\d{1,2}-\d{1,2}/.test(str)) return false;
  const d = new Date(str);
  return d instanceof Date && !Number.isNaN(d.getTime());
};

export const focusAmount = (e) => {
  if (amountToNumber(e.target.value) === 0.00) {
    e.target.select();
  }
};

export const onFocusAutocomplete = (e) => {
  setTimeout(() => {
    e.target.select();
  }, 200);
};

export const focusNextField = (self, e, ref) => {
  e.preventDefault();
  self.$refs[`${ref}`].focus();
};

export const mergeByIdNewAtTop = (existingList, newItem) => {
  let wasInList = false;
  let newList = existingList.map((t) => {
    if ((t.id && t.id === newItem.id) || (t._id && t._id === newItem._id)) {
      wasInList = true;
      return newItem;
    }
    return t;
  });
  if (!wasInList) {
    newList = [newItem, ...newList];
  }
  return newList;
};

export const getItemsIndexByID = (itemsList, item) => itemsList.map((t) => t.id).indexOf(item.id);

export const mimeTypeByFileName = (fileName) => {
  const match = (fileName || '')
    .toLowerCase().match(/\.([a-zA-Z0-9]+)$/);
  const fileExtension = match ? match[1] : ''; // asDf.XyZ -> xyz
  switch (fileExtension) {
    case 'pdf': {
      return 'application/pdf';
    }
    case 'png': {
      return 'image/png';
    }
    case 'jpeg':
    case 'jpg': {
      return 'image/jpeg';
    }
    case 'tiff':
    case 'tif': {
      return 'image/tiff';
    }
    case 'avif': {
      return 'image/avif';
    }
    default: {
      return 'application/octet-stream';
    }
  }
};
export const changeTransPaymentDetails = (dupDetails) => {
  const dupTrans = JSON.parse(JSON.stringify(dupDetails));
  if (Object.prototype.toString.call(dupTrans) === '[object Object]') {
    if (Array.isArray(dupTrans.paymentDetails)) {
      dupTrans.paymentDetails = dupTrans.paymentDetails.map((paymentDetails) => {
        const dupPaymentDetail = {};
        dupPaymentDetail.paymentType = paymentDetails.paymentType;
        dupPaymentDetail.amount = paymentDetails.amount;
        switch (paymentDetails.paymentType) {
          case 'CHECK':
            dupPaymentDetail.checkNumber = paymentDetails.checkNumber;
            dupPaymentDetail.checkPaidBy = paymentDetails.checkPaidBy;
            break;
          case 'CASH':
            dupPaymentDetail.cashReceived = paymentDetails.cashReceived;
            dupPaymentDetail.cashChange = paymentDetails.cashChange;
            break;
          case 'CREDIT':
            dupPaymentDetail.creditType = paymentDetails.creditType;
            break;
          case 'EFT':
            dupPaymentDetail.eftType = paymentDetails.eftType;
            break;
          default:
            break;
        }
        return dupPaymentDetail;
      });
    }
  }
  return dupTrans;
};

export const isTransactionUpdated = ({ rootGetters }, { item, baseCrudKey, changeFunction }) => {
  const cleanedItem = { ...item };
  const transactionState = rootGetters['base/crud/items'](baseCrudKey);
  if (cleanedItem._id && transactionState && transactionState.length > 0) {
    let existingTrans = transactionState.find((t) => t._id === cleanedItem._id);
    if (existingTrans) {
      delete existingTrans.hasFiles;
      existingTrans = changeFunction(existingTrans);
      if (existingTrans) {
        cleanedItem.amount = amountToNumber(cleanedItem.amount);
        existingTrans.amount = amountToNumber(existingTrans.amount);
        if ((JSON.stringify(existingTrans) === JSON.stringify(cleanedItem))) {
          console.info('no need to save...');
          return true;
        }
      }
    }
  }
  return false;
};

export const sanitizeFilename = (filename) => (filename || '').replace(/[^a-z0-9]/gi, '_');

export const isEmptyObject = (obj) => !(obj != null && typeof obj === 'object' && Object.keys(obj).length > 0);

/** Deep snapshot for edit baseline; preserves Date (unlike JSON clone). */
export const cloneItem = (source) => {
  if (typeof window !== 'undefined' && typeof window.structuredClone === 'function') {
    return window.structuredClone(source);
  }
  // Fallback to JSON clone if structuredClone is not available (e.g., in older browsers). Note that this will not preserve Date objects, so they will be converted to strings.
  const copy = JSON.parse(JSON.stringify(source));
  console.warn('structuredClone not available; using JSON clone for item clone, which may not preserve Date objects correctly');
  return copy;
};

export const fieldsToGqlString = (fields) => {
  let string = '';
  Object.keys(fields).forEach((key) => {
    const value = fields[key];
    if (!isEmptyObject(value)) {
      string += `${key} {${fieldsToGqlString(value)}},`;
    } else {
      string += `${key},`;
    }
  });
  return string;
};

export const parseJson = (response) => {
  let parsedResponse;
  let isResParsed;
  try {
    parsedResponse = JSON.parse(response);
    isResParsed = true;
  } catch (e) {
    parsedResponse = response;
    isResParsed = false;
  }
  return [parsedResponse, isResParsed];
};

export const base64FromArrayBuffer = async (data) => {
  const base64url = await new Promise((r) => {
    const reader = new FileReader();
    reader.onload = () => r(reader.result);
    reader.readAsDataURL(new Blob([data]));
  });
  return base64url.split(',', 2)[1];
};

export const extractId = (item) => {
  if (item == null) return null;
  if (typeof item === 'object' && (item._id !== undefined || item.id !== undefined)) return (item._id || item.id);
  return item;
};

export const checkStringEmptiness = (stringVal) => (stringVal == null || stringVal === '');

export const resetWindow = (self, initdata, retainData = {}) => {
  const updatedObj = { ...initdata, ...retainData };
  Object.assign(self.$data, { ...updatedObj });
};

export const defaultOverlayConfig = () => ({ value: 0, flag: false });

export const defaultGridLoading = () => ({ text: '', flag: false });

export const incrementOverlay = (self, total, current) => {
  const dupSelf = self;
  dupSelf.overlay.value = parseInt((100 * (current + 1)) / total, 10);
};
export const manageGridLoading = (self, flag, text = '') => {
  const dupSelf = self;
  dupSelf.gridLoading.flag = flag;
  dupSelf.gridLoading.text = text;
};

export default {
  isAltC,
  isAltR,
  isAltE,
  isTabOrPlus,
  isCreditFnKey,
  isCashFnKey,
  focusAmount,
  focusBySelector,
  amountFormat,
  amountFormatCanbeEmpty,
  camelCaseToWords,
  isDate,
  isIsoDate,
  amountToNumber,
  selectInputTextBySelector,
  getItemsIndexByID,
  mimeTypeByFileName,
  sanitizeFilename,
  parseJson,
  isEmptyObject,
  cloneItem,
  fieldsToGqlString,
  onFocusAutocomplete,
  focusNextField,
  checkStringEmptiness,
  extractId,
  defaultGridLoading,
  defaultOverlayConfig,
  incrementOverlay,
  manageGridLoading,
  camelCaseEachToWords,
};
